import { createContext, useMemo, useContext, useState } from 'react';
import cx from 'classnames';
import { useIsKeyboardNavigationState } from '../../hooks/useKeyboardNavigation';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const BoxSelectorContext = /*#__PURE__*/createContext(undefined);
function BoxSelector({
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  className,
  name,
  value,
  disabled,
  children,
  mktSelector,
  type = 'radio',
  isValid,
  columnClassName: _columnClassName,
  ...wrapperProps
}) {
  const providerData = useMemo(() => {
    return {
      name,
      value,
      type,
      onFocus,
      onBlur,
      onChange,
      disabled,
      isValid: isValid !== false
    };
  }, [name, value, type, onFocus, onBlur, onChange, disabled, isValid]);
  return /*#__PURE__*/_jsx(BoxSelectorContext.Provider, {
    value: providerData,
    children: /*#__PURE__*/_jsx("div", {
      className: cx('box-selector', 'row', className),
      "data-mkt-selector": mktSelector,
      ...wrapperProps,
      children: children
    })
  });
}
function BoxColumn({
  className = 'col',
  children
}) {
  return /*#__PURE__*/_jsx("div", {
    className: cx('box-selector__column', className),
    children: children
  });
}
function BoxItem({
  children,
  value,
  className,
  selectedClassName = '',
  disabledClassName = '',
  mktSelector,
  qaSelector,
  disabled: itemDisabled = false
}) {
  const {
    id,
    isSelected,
    disabled,
    selectedValue,
    name,
    type,
    onChange,
    classNames,
    onFocus: onFocusHandler,
    onBlur: onBlurHander
  } = useBoxSelectorItem({
    value,
    className,
    selectedClassName,
    disabledClassName,
    disabled: itemDisabled
  });
  const usableValue = typeof value === 'boolean' ? String(value) : value;
  return /*#__PURE__*/_jsxs("label", {
    htmlFor: id,
    className: classNames,
    "data-qa-value": value,
    "data-mkt-selector": mktSelector,
    "data-qa-selector": qaSelector,
    "aria-disabled": disabled,
    children: [typeof children === 'function' ?
    //@ts-expect-error
    children({
      isSelected,
      selectedValue
    }) : children, /*#__PURE__*/_jsx("input", {
      className: "visually-hidden",
      id: id,
      type: type,
      name: name,
      value: usableValue
      //@ts-expect-error
      ,
      checked: isSelected,
      disabled: disabled,
      onChange: e => {
        if (!disabled) {
          onChange(value, e);
        }
      },
      onBlur: onBlurHander,
      onFocus: onFocusHandler
    })]
  });
}
function useBoxSelectorItem(props) {
  const context = useContext(BoxSelectorContext);
  const isTabbing = useIsKeyboardNavigationState();
  const [hasFocus, setHasFocus] = useState(false);
  if (context === undefined) {
    throw new Error('useBoxSelectorItem must be used within a BoxSelector element');
  }
  const {
    name,
    value: selectedValue,
    type,
    onBlur,
    onFocus,
    disabled,
    isValid
  } = context;
  const {
    value,
    className,
    selectedClassName = '',
    disabledClassName = ''
  } = props;
  const isDisabled = disabled || props.disabled;
  const hasFocusRing = !isDisabled && isTabbing && hasFocus;
  const id = `${name}_${value}`;
  const isSelected = type === 'checkbox' ? Array.isArray(selectedValue) ? selectedValue.includes(value) : selectedValue : value === selectedValue;
  const classNames = cx('box-selector__item', className, {
    'box-selector__item--is-selected': !isDisabled && isSelected,
    'box-selector__item--is-disabled': isDisabled,
    'box-selector__item--has-ring': hasFocusRing,
    'box-selector__item--has-error': !isValid,
    [selectedClassName]: isSelected,
    [disabledClassName]: isDisabled
  });
  function onBlurHander(e) {
    if (!isDisabled) {
      onBlur?.(e);
      setHasFocus(false);
    }
  }
  function onFocusHandler(e) {
    if (!isDisabled) {
      onFocus?.(e);
      setHasFocus(true);
    }
  }
  return {
    ...context,
    id,
    isSelected,
    selectedValue,
    name,
    value: props.value,
    onBlur: onBlurHander,
    onFocus: onFocusHandler,
    hasFocus: hasFocus,
    hasFocusRing,
    classNames,
    isValid,
    disabled: isDisabled
  };
}
export default BoxSelector;
export { BoxItem, BoxColumn, BoxSelector };